import { useMemo, useState } from 'react';

import { organizerSidebar } from '@/config/sidebarConfig.ts';
import { getCompetitionsApi } from '@/lib/api/competitionsApi';
import { fetchAllPaymentsForOrg, getPaymentApi, getPaymentsApi } from '@/lib/api/paymentsApi';
import { type ExportColumn, type ExportFormat, datedFilename, exportData } from '@/lib/export/export';
import { getPageNumbers } from '@/lib/pagination';
import { queryKeys } from '@/lib/query/queryKeys';
import { PAY_STATUS_CONFIG } from '@/lib/status-config';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr, formatEur } from '@shared';
import { PaymentStatus, RefundRequestStatus } from '@shared/enums/domain.enum';
import type { Payment } from '@shared/types/entities';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Banknote,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Euro,
  Eye,
  RotateCcw,
  TrendingUp,
  Wallet,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../components/layout/DashboardLayout';
import Dialog from '../../components/ui/Dialog';
import PageHeader from '../../components/ui/PageHeader';
import StatCard from '../../components/ui/StatCard';

// ─── Constants ──────────────────────────────────────────────────

const PAGE_SIZE = 20;

// ─── Badge config ────────────────────────────────────────────────

// ─── Helpers ─────────────────────────────────────────────────────

function computeMonthlyRevenue(payments: Payment[]): { key: string; label: string; amount: number }[] {
  const now = new Date();
  const months = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
    return {
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('fr-FR', { month: 'short' }),
      amount: 0,
    };
  });
  for (const p of payments) {
    if (p.status !== PaymentStatus.COMPLETED) continue;
    const k = p.createdAt.slice(0, 7);
    const m = months.find(x => x.key === k);
    if (m) m.amount += p.amount;
  }
  return months;
}

// ─── Payment Detail Modal ────────────────────────────────────────

interface PaymentDetailModalProps {
  paymentId: number | null;
  onClose: () => void;
}

function PaymentDetailModal({ paymentId, onClose }: PaymentDetailModalProps) {
  const { data: payment, isLoading } = useQuery<Payment>({
    queryKey: queryKeys.payments.detail(paymentId!),
    queryFn: () => getPaymentApi(paymentId!),
    enabled: paymentId !== null,
  });

  const cfg = payment ? PAY_STATUS_CONFIG[payment.status] : null;

  return (
    <Dialog
      open={!!paymentId}
      onClose={onClose}
      title='Détail du paiement'
      className='max-w-lg max-h-[90vh] overflow-y-auto'
    >
      {/* Header */}
      <div className='sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl'>
        <div>
          <h2 className='text-lg font-bold text-slate-900'>Détail du paiement</h2>
          {payment && <p className='text-xs font-mono text-slate-400 mt-0.5'>{payment.uid}</p>}
        </div>
        <button onClick={onClose} aria-label='Fermer' className='p-2 rounded-lg hover:bg-slate-100 transition-colors'>
          <X className='w-5 h-5 text-slate-500' />
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className='p-6 space-y-4'>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className='h-12 animate-pulse rounded-xl bg-slate-100' />
          ))}
        </div>
      )}

      {/* Content */}
      {!isLoading && payment && (
        <div className='p-6 space-y-5'>
          {/* Status + amount */}
          <div className='flex items-center gap-3 flex-wrap'>
            {cfg && (
              <span
                className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold ${cfg.bg} ${cfg.text}`}
              >
                {cfg.label}
              </span>
            )}
            <span className='text-2xl font-bold text-slate-900'>{formatEur(payment.amount)}</span>
          </div>

          {/* Competition + Team */}
          <div className='grid grid-cols-2 gap-3'>
            <div className='bg-slate-50 rounded-xl p-4'>
              <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5'>Concours</p>
              <p className='text-sm font-semibold text-slate-900 leading-snug'>{payment.competition?.name ?? '—'}</p>
              {payment.competition?.date && (
                <p className='text-xs text-slate-400 mt-1'>{formatDateAbbreviatedFr(payment.competition.date)}</p>
              )}
            </div>
            <div className='bg-slate-50 rounded-xl p-4'>
              <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5'>Équipe</p>
              <p className='text-sm font-semibold text-slate-900 leading-snug'>{payment.team?.name ?? '—'}</p>
            </div>
          </div>

          {/* Financial breakdown */}
          <div className='bg-slate-50 rounded-xl p-4 space-y-2.5'>
            <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide'>Détail financier</p>
            <div className='flex justify-between text-sm'>
              <span className='text-slate-600'>Montant total</span>
              <span className='font-semibold text-slate-900'>{formatEur(payment.amount)}</span>
            </div>
            {payment.applicationFeeAmount != null && (
              <>
                <div className='flex justify-between text-sm'>
                  <span className='text-slate-600'>Commission BoulesConnect</span>
                  <span className='font-semibold text-red-600'>−{formatEur(payment.applicationFeeAmount)}</span>
                </div>
                <div className='flex justify-between text-sm border-t border-slate-200 pt-2.5'>
                  <span className='font-semibold text-slate-700'>Net encaissé</span>
                  <span className='font-bold text-emerald-700'>
                    {formatEur(payment.amount - payment.applicationFeeAmount)}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Refund block */}
          {payment.status === PaymentStatus.REFUNDED && (
            <div className='bg-purple-50 rounded-xl p-4 space-y-1.5'>
              <p className='text-xs font-semibold text-purple-700 uppercase tracking-wide'>Remboursement</p>
              {payment.refundAmount != null && (
                <p className='text-sm font-semibold text-purple-900'>{formatEur(payment.refundAmount)}</p>
              )}
              {payment.refundedAt && (
                <p className='text-xs text-purple-600'>Le {formatDateAbbreviatedFr(payment.refundedAt)}</p>
              )}
            </div>
          )}

          {/* Refund request block */}
          {payment.refundRequest && payment.status !== PaymentStatus.REFUNDED && (
            <div
              className={`rounded-xl p-4 space-y-1.5 ${
                payment.refundRequest.status === RefundRequestStatus.PENDING
                  ? 'bg-amber-50'
                  : payment.refundRequest.status === RefundRequestStatus.APPROVED
                    ? 'bg-blue-50'
                    : 'bg-red-50'
              }`}
            >
              <div className='flex items-center gap-2'>
                <RotateCcw
                  className={`w-4 h-4 ${
                    payment.refundRequest.status === RefundRequestStatus.PENDING
                      ? 'text-amber-600'
                      : payment.refundRequest.status === RefundRequestStatus.APPROVED
                        ? 'text-blue-600'
                        : 'text-red-600'
                  }`}
                />
                <p
                  className={`text-xs font-semibold uppercase tracking-wide ${
                    payment.refundRequest.status === RefundRequestStatus.PENDING
                      ? 'text-amber-700'
                      : payment.refundRequest.status === RefundRequestStatus.APPROVED
                        ? 'text-blue-700'
                        : 'text-red-700'
                  }`}
                >
                  Demande de remboursement —{' '}
                  {payment.refundRequest.status === RefundRequestStatus.PENDING
                    ? 'En attente'
                    : payment.refundRequest.status === RefundRequestStatus.APPROVED
                      ? 'Approuvée'
                      : 'Refusée'}
                </p>
              </div>
              {payment.refundRequest.reason && <p className='text-sm text-slate-700'>{payment.refundRequest.reason}</p>}
              <p className='text-xs text-slate-400'>
                Demandé le {formatDateAbbreviatedFr(payment.refundRequest.createdAt)}
              </p>
            </div>
          )}

          {/* Stripe reference */}
          <div className='bg-slate-50 rounded-xl p-4'>
            <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2'>Référence Stripe</p>
            <p className='text-xs font-mono text-slate-700 break-all leading-relaxed'>
              {payment.stripePaymentIntentId}
            </p>
          </div>

          {/* Dates */}
          <div className='text-xs text-slate-400 space-y-1'>
            {payment.paidAt && <p>Payé le {formatDateAbbreviatedFr(payment.paidAt)}</p>}
            <p>Créé le {formatDateAbbreviatedFr(payment.createdAt)}</p>
          </div>
        </div>
      )}
    </Dialog>
  );
}

// ─── Main Page ───────────────────────────────────────────────────

export default function OrganizerPayments() {
  const orgId = useAuthStore(s => s.user)?.organizationsAdmin[0]?.organizationId;

  // ─── Filters ───
  const [selectedCompetitionId, setSelectedCompetitionId] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<PaymentStatus | ''>('');
  const [page, setPage] = useState(1);

  // ─── Modal ───
  const [openPaymentId, setOpenPaymentId] = useState<number | null>(null);

  // ─── CSV export ───
  const [exporting, setExporting] = useState(false);

  // ─── Competitions (filter dropdown) ───
  const { data: competitionsData } = useQuery({
    queryKey: queryKeys.competitions.list({ organizationId: orgId, limit: 100 }),
    queryFn: () => getCompetitionsApi({ organizationId: orgId, limit: 100 }),
    enabled: !!orgId,
    select: res => res.data,
  });
  const competitions = useMemo(() => competitionsData ?? [], [competitionsData]);

  // ─── Stats query (tous les paiements de l'org, pagination automatique) ───
  const { data: statsPaymentsData } = useQuery({
    queryKey: queryKeys.payments.list({ organizationId: orgId, _all: true }),
    queryFn: () => fetchAllPaymentsForOrg(orgId!),
    enabled: !!orgId,
  });
  const statsPayments: Payment[] = useMemo(() => statsPaymentsData ?? [], [statsPaymentsData]);

  // ─── List query (paginated, with filters) ───
  const listParams = {
    organizationId: orgId,
    ...(selectedCompetitionId && { competitionId: Number(selectedCompetitionId) }),
    ...(statusFilter && { status: statusFilter }),
    page,
    limit: PAGE_SIZE,
  };
  const {
    data: listData,
    isLoading: listLoading,
    isFetching: listFetching,
  } = useQuery({
    queryKey: queryKeys.payments.list(listParams as Record<string, unknown>),
    queryFn: () => getPaymentsApi(listParams),
    enabled: !!orgId,
    placeholderData: keepPreviousData,
  });

  const payments: Payment[] = listData?.data ?? [];
  const total = listData?.meta?.total ?? 0;
  const totalPages = listData?.meta?.totalPages ?? 1;
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  // ─── Aggregate stats from statsData ───
  const totalRevenue = useMemo(
    () => statsPayments.filter(p => p.status === PaymentStatus.COMPLETED).reduce((sum, p) => sum + p.amount, 0),
    [statsPayments],
  );
  const pendingAmount = useMemo(
    () => statsPayments.filter(p => p.status === PaymentStatus.PENDING).reduce((sum, p) => sum + p.amount, 0),
    [statsPayments],
  );
  const refundedAmount = useMemo(
    () =>
      statsPayments
        .filter(p => p.status === PaymentStatus.REFUNDED)
        .reduce((sum, p) => sum + (p.refundAmount ?? p.amount), 0),
    [statsPayments],
  );
  const pendingCount = useMemo(
    () => statsPayments.filter(p => p.status === PaymentStatus.PENDING).length,
    [statsPayments],
  );
  const refundedCount = useMemo(
    () => statsPayments.filter(p => p.status === PaymentStatus.REFUNDED).length,
    [statsPayments],
  );
  const completedCount = useMemo(
    () => statsPayments.filter(p => p.status === PaymentStatus.COMPLETED).length,
    [statsPayments],
  );

  // ─── Monthly chart data ───
  const monthlyData = useMemo(() => computeMonthlyRevenue(statsPayments), [statsPayments]);
  const maxMonthlyAmount = Math.max(...monthlyData.map(m => m.amount), 1);

  // ─── Export ───
  const orgPayExportColumns: ExportColumn<Payment>[] = [
    {
      header: 'Date',
      value: p => (p.paidAt ? formatDateAbbreviatedFr(p.paidAt) : formatDateAbbreviatedFr(p.createdAt)),
    },
    { header: 'Référence', value: 'uid' },
    { header: 'Concours', value: p => p.competition?.name ?? '' },
    { header: 'Équipe', value: p => p.team?.name ?? '' },
    { header: 'Montant (€)', value: p => p.amount / 100, numFmt: '#,##0.00' },
    { header: 'Commission (€)', value: p => (p.applicationFeeAmount ?? 0) / 100, numFmt: '#,##0.00' },
    { header: 'Net (€)', value: p => (p.amount - (p.applicationFeeAmount ?? 0)) / 100, numFmt: '#,##0.00' },
    { header: 'Statut', value: p => PAY_STATUS_CONFIG[p.status].label },
  ];

  async function handleExport(format: ExportFormat = 'csv') {
    if (!orgId) return;
    setExporting(true);
    try {
      const rows: Payment[] = await fetchAllPaymentsForOrg(orgId);
      await exportData(rows, orgPayExportColumns, datedFilename('paiements', format), format, {
        sheetName: 'Paiements',
      });
      toast.success(`${rows.length} paiements exportés`);
    } catch {
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  }

  function handleCompetitionChange(value: string) {
    setSelectedCompetitionId(value ? Number(value) : '');
    setPage(1);
  }

  function handleStatusChange(value: string) {
    setStatusFilter(value as PaymentStatus | '');
    setPage(1);
  }

  return (
    <DashboardLayout sidebarConfig={organizerSidebar}>
      <div className='p-8'>
        <PageHeader
          title='Paiements'
          subtitle='Suivi de vos revenus'
          actions={
            <div className='flex items-center gap-2'>
              <button
                onClick={() => void handleExport('csv')}
                disabled={exporting || !orgId}
                aria-label='Exporter les paiements en CSV'
                className='flex items-center gap-2 px-5 py-2 bg-white border border-slate-200 rounded-xl hover:border-blue-300 transition disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <Download className='w-4 h-4 text-slate-600' />
                <span className='text-sm font-semibold text-slate-700'>{exporting ? 'Export…' : 'CSV'}</span>
              </button>
              <button
                onClick={() => void handleExport('xlsx')}
                disabled={exporting || !orgId}
                aria-label='Exporter les paiements en Excel'
                className='flex items-center gap-2 px-5 py-2 bg-white border border-slate-200 rounded-xl hover:border-blue-300 transition disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <Download className='w-4 h-4 text-slate-600' />
                <span className='text-sm font-semibold text-slate-700'>{exporting ? 'Export…' : 'Excel'}</span>
              </button>
            </div>
          }
        />

        {/* ─── Stats cards ── */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
          <StatCard
            icon={Euro}
            iconBg='bg-blue-100'
            iconColor='text-blue-600'
            label='Total encaissé'
            value={formatEur(totalRevenue)}
            subtitle={`${completedCount} paiement${completedCount !== 1 ? 's' : ''} complété${completedCount !== 1 ? 's' : ''}`}
          />
          <StatCard
            icon={Clock}
            iconBg='bg-amber-100'
            iconColor='text-amber-600'
            label='En attente'
            value={formatEur(pendingAmount)}
            subtitle={`${pendingCount} paiement${pendingCount !== 1 ? 's' : ''} en cours`}
          />
          <StatCard
            icon={RotateCcw}
            iconBg='bg-purple-100'
            iconColor='text-purple-600'
            label='Remboursé'
            value={formatEur(refundedAmount)}
            subtitle={`${refundedCount} remboursement${refundedCount !== 1 ? 's' : ''}`}
          />
          <StatCard
            icon={Banknote}
            iconBg='bg-emerald-100'
            iconColor='text-emerald-600'
            label='Total transactions'
            value={String(statsPayments.length || '—')}
            subtitle='Tous statuts confondus'
          />
        </div>

        {/* ─── Revenue chart ── */}
        <div className='bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-8 mb-8 text-white'>
          <div className='flex items-center justify-between mb-6'>
            <div>
              <h2 className='text-xl font-bold flex items-center gap-2'>
                <TrendingUp className='w-6 h-6' />
                Revenus mensuels
              </h2>
              <p className='text-blue-200 text-sm mt-1'>Encaissements des 6 derniers mois</p>
            </div>
            <div className='text-right'>
              <p className='text-blue-200 text-xs mb-0.5'>Total période</p>
              <p className='text-xl font-bold'>{formatEur(monthlyData.reduce((s, m) => s + m.amount, 0))}</p>
            </div>
          </div>

          <div className='flex items-end justify-between gap-3'>
            {monthlyData.map((bar, i) => {
              const heightPx =
                maxMonthlyAmount > 0 ? Math.max((bar.amount / maxMonthlyAmount) * 140, bar.amount > 0 ? 10 : 3) : 3;
              return (
                <div key={i} className='flex-1 flex flex-col items-center gap-2'>
                  {bar.amount > 0 ? (
                    <p className='text-xs text-white/70'>{Math.round(bar.amount / 100)} €</p>
                  ) : (
                    <p className='text-xs text-transparent select-none'>0</p>
                  )}
                  <div
                    className='w-full bg-white/30 rounded-t-lg transition-all duration-500'
                    style={{ height: `${heightPx}px` }}
                  />
                  <span className='text-xs text-blue-200'>{bar.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ─── Payment table ── */}
        <div className='bg-white rounded-2xl border border-slate-200'>
          {/* Filters bar */}
          <div className='flex items-center justify-between flex-wrap gap-3 px-6 py-4 border-b border-slate-200'>
            <h2 className='text-lg font-bold text-slate-900 shrink-0'>Historique des paiements</h2>
            <div className='flex items-center gap-3 flex-wrap'>
              <select
                value={selectedCompetitionId}
                onChange={e => handleCompetitionChange(e.target.value)}
                className='px-3 py-2 border border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm text-slate-700 bg-white'
              >
                <option value=''>Tous les concours</option>
                {competitions.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={e => handleStatusChange(e.target.value)}
                className='px-3 py-2 border border-slate-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm text-slate-700 bg-white'
              >
                <option value=''>Tous les statuts</option>
                {Object.entries(PAY_STATUS_CONFIG).map(([value, cfg]) => (
                  <option key={value} value={value}>
                    {cfg.label}
                  </option>
                ))}
              </select>
              {listFetching && !listLoading && <span className='text-xs text-slate-400'>Actualisation…</span>}
            </div>
          </div>

          {/* Table header */}
          <div className='grid grid-cols-12 gap-4 px-6 py-3 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider'>
            <div className='col-span-2'>Date</div>
            <div className='col-span-3'>Concours</div>
            <div className='col-span-3'>Équipe</div>
            <div className='col-span-2'>Montant</div>
            <div className='col-span-1'>Statut</div>
            <div className='col-span-1 text-right'>Détail</div>
          </div>

          {/* Table body */}
          {listLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className='grid grid-cols-12 gap-4 px-6 py-4 border-b border-slate-50'>
                <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                <div className='col-span-3 h-4 animate-pulse rounded bg-slate-200' />
                <div className='col-span-3 h-4 animate-pulse rounded bg-slate-200' />
                <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                <div className='col-span-1 h-4 animate-pulse rounded bg-slate-200' />
                <div className='col-span-1 h-4 animate-pulse rounded bg-slate-200' />
              </div>
            ))
          ) : payments.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-16 text-slate-400 gap-3'>
              <Wallet className='w-10 h-10 opacity-30' />
              <p className='text-sm font-medium'>
                Aucun paiement{statusFilter || selectedCompetitionId ? ' avec ces filtres' : ''}
              </p>
            </div>
          ) : (
            payments.map(payment => {
              const cfg = PAY_STATUS_CONFIG[payment.status];
              const dateStr = payment.paidAt
                ? formatDateAbbreviatedFr(payment.paidAt)
                : formatDateAbbreviatedFr(payment.createdAt);
              return (
                <div
                  key={payment.id}
                  className='grid grid-cols-12 gap-4 px-6 py-4 border-b border-slate-50 items-center hover:bg-slate-50 transition'
                >
                  <div className='col-span-2 text-sm text-slate-600'>{dateStr}</div>

                  <div className='col-span-3 min-w-0'>
                    <p className='text-sm font-semibold text-slate-900 truncate'>{payment.competition?.name ?? '—'}</p>
                    {payment.competition?.date && (
                      <p className='text-xs text-slate-400 mt-0.5'>
                        {formatDateAbbreviatedFr(payment.competition.date)}
                      </p>
                    )}
                  </div>

                  <div className='col-span-3 min-w-0'>
                    <p className='text-sm text-slate-700 truncate'>{payment.team?.name ?? '—'}</p>
                  </div>

                  <div className='col-span-2'>
                    <p className='text-sm font-bold text-slate-900'>{formatEur(payment.amount)}</p>
                    {payment.applicationFeeAmount != null && payment.applicationFeeAmount > 0 && (
                      <p className='text-xs text-slate-400 mt-0.5'>Comm. {formatEur(payment.applicationFeeAmount)}</p>
                    )}
                  </div>

                  <div className='col-span-1'>
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}
                    >
                      {cfg.label}
                    </span>
                  </div>

                  <div className='col-span-1 flex items-center justify-end'>
                    <button
                      onClick={() => setOpenPaymentId(payment.id)}
                      aria-label='Voir le détail de ce paiement'
                      title='Voir le détail'
                      className='p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition'
                    >
                      <Eye className='w-4 h-4' />
                    </button>
                  </div>
                </div>
              );
            })
          )}

          {/* Pagination */}
          {total > 0 && (
            <div className='flex items-center justify-between border-t border-slate-200 px-6 py-4'>
              <p className='text-sm text-slate-500'>
                Affichage de <span className='font-semibold text-slate-700'>{from}</span> à{' '}
                <span className='font-semibold text-slate-700'>{to}</span> sur{' '}
                <span className='font-semibold text-slate-700'>{total.toLocaleString('fr-FR')}</span> paiements
              </p>
              {totalPages > 1 && (
                <div className='flex items-center gap-1'>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    aria-label='Page précédente'
                    className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                  >
                    <ChevronLeft className='h-4 w-4' />
                  </button>
                  {getPageNumbers(page, totalPages).map((p, i) =>
                    p === '...' ? (
                      <span key={`dots-${i}`} className='px-2 text-slate-400'>
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p as number)}
                        className={`rounded-lg px-3 py-1 text-sm font-semibold transition ${
                          p === page ? 'bg-blue-600 text-white' : 'border border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        {p}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    aria-label='Page suivante'
                    className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                  >
                    <ChevronRight className='h-4 w-4' />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Payment detail modal */}
      <PaymentDetailModal paymentId={openPaymentId} onClose={() => setOpenPaymentId(null)} />
    </DashboardLayout>
  );
}
